import { useEffect, useState } from 'react';

import { cx } from '@emotion/css';
import DownloadIcon from '@mui/icons-material/Download';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { CircularProgress, Grid } from '@mui/material';
import { GetOneParams, useDataProvider, useNotify, Button } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { DownloadedFile, DownloadFileResponse } from 'src/models';
import { BaseLayout, Spacing } from 'src/styles';

import { FileViewerModal } from '../FileViewerModal/FileViewerModal';
import { MDeleteButton } from '../MDeleteButton/MDeleteButton';
import { MEditButton } from '../MEditButton/MEditButton';

export interface FileDetailsProps {
  id: string;
  name: string;
  resource: {
    name: DataProviderResource;
    params: GetOneParams;
  };
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const FileDetails = ({ id, name, resource, onEdit, onDelete }: FileDetailsProps) => {
  const [file, setFile] = useState<DownloadedFile | null>(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isViewLoading, setIsViewLoading] = useState<boolean>(false);
  const [isViewDocumentVisible, setIsViewDocumentVisible] = useState<boolean>(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const openSaveFileDialog = (url: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.style.display = 'none';
    downloadLink.download = resource.params.meta?.name; // Specify the desired filename

    // Append the link to the document and trigger the download
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up by removing the link
    document.body.removeChild(downloadLink);
  };

  const downloadFile = async () => {
    return dataProvider.getOne<DownloadFileResponse>(resource.name, resource.params);
  };

  const onDownload = async () => {
    try {
      setIsDownloadLoading(true);

      if (!file) {
        const response = await downloadFile();
        setFile(response.data.file);
        openSaveFileDialog(response.data.file.url);

        return;
      }
      openSaveFileDialog(file.url);
    } catch (error) {
      notify(`Error downloading file. Received ${JSON.stringify(error)}`, {
        type: 'error',
      });
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const _onView = async () => {
    try {
      setIsViewLoading(true);

      if (!file) {
        const response = await downloadFile();
        setFile(response.data.file);
        setIsViewDocumentVisible(true);

        return;
      }
    } catch (error) {
      notify(`Error opening file. Received ${JSON.stringify(error)}`, {
        type: 'error',
      });
      setIsViewDocumentVisible(false);
    } finally {
      setIsViewLoading(false);
    }
  };

  const onFileViewerModalClose = () => {
    setIsViewDocumentVisible(false);
  };

  useEffect(() => {
    if (file) {
      URL.revokeObjectURL(file.url);
    }
  }, []);

  return (
    <Grid item md={12} className={cx(BaseLayout.card, Spacing.my16)}>
      <Grid container justifyContent='center' alignItems='center'>
        <Grid item md={1}>
          <FilePresentIcon />
        </Grid>
        <Grid item md={8}>
          {name}
        </Grid>
        <Grid item md={3}>
          <Button label='Download' onClick={onDownload} disabled={isDownloadLoading}>
            {isDownloadLoading ? <CircularProgress color='inherit' size='1em' /> : <DownloadIcon />}
          </Button>
          <Button label='View' onClick={_onView} disabled={isViewLoading}>
            {isViewLoading ? <CircularProgress color='inherit' size='1em' /> : <FileOpenIcon />}
          </Button>
          {onEdit && <MEditButton onClick={() => onEdit(id)} />}
          {onDelete && <MDeleteButton onClick={() => onDelete(id)} />}
        </Grid>
      </Grid>
      {isViewDocumentVisible && file && <FileViewerModal onClose={onFileViewerModalClose} file={file} />}
    </Grid>
  );
};
